/**
 * `useAssistantIdentity` — the shared read side of the §6.2 assistant record.
 *
 * One place to `identity.get` + re-read on `identity.updated`, so the hero, the
 * channel summary and (later) the chat header all show the same live name /
 * avatar without each re-implementing the fetch. WRITES stay in the identity
 * editor (`IdentityLayout` today; the S3 hero picker next) — this hook is
 * read-only.
 *
 * `backendPending` mirrors `IdentityLayout`'s method-not-found handling: a
 * runtime that predates `identity.get` should show the graceful "Alex" default,
 * not an error.
 */
import { useCallback, useEffect, useState } from "react";

import { useGatewayStore } from "@/stores/useGatewayStore";
import { useLexEventStream } from "@/hooks/useEventStream";
import { ASSISTANT_DEFAULT_NAME } from "@/lib/contacts-logic";
import type { AssistantIdentity, Contact } from "@/types/contacts";

/** Platform default assistant name (operator 2026-07-27: the hero must read
 *  "Alex", never the brand). Defined in the pure-logic module so the previews
 *  there and this hook cannot drift apart; re-exported for existing callers. */
export { ASSISTANT_DEFAULT_NAME };

/** Names the runtime historically provisioned as its OWN default ("Lexiun",
 *  assistant-identity-store.ts:90). A record carrying one of these was never
 *  actually named by the lawyer, so the SPA maps it to the platform default —
 *  the display-side half of the §9.3a "Alex platform-wide" handoff (the runtime
 *  fallback + lexserver From-grammar halves are queued on the bus). */
const LEGACY_DEFAULT_NAMES = new Set(["lexiun"]);

/** True when the assistant was never really named — empty or a legacy runtime
 *  default. The DEFAULT identity gets the full default look: the name "Alex"
 *  AND Alex's human default avatar (even over a seeded emoji). */
export function isDefaultAssistantIdentity(identity: AssistantIdentity | null): boolean {
  const n = identity?.name?.trim().toLowerCase();
  return !n || LEGACY_DEFAULT_NAMES.has(n);
}

export function assistantDisplayName(identity: AssistantIdentity | null): string {
  return isDefaultAssistantIdentity(identity)
    ? ASSISTANT_DEFAULT_NAME
    : (identity as AssistantIdentity).name.trim();
}

/**
 * The name the LAWYER chose, or `null` when there isn't one yet.
 *
 * 🔴 **NOT a variant of `assistantDisplayName()` — its OPPOSITE, and the
 * difference is the whole point.** `assistantDisplayName` exists to always
 * produce something renderable, so it answers `"Alex"` for an assistant nobody
 * has named. That is correct for a heading or an avatar and **catastrophic for
 * an instruction**: interpolated into the BotFather step it tells a lawyer to
 * name his bot "Alex" — and the lawyer this was reported for is **Alexandre**,
 * whose own how-to text forbids naming the bot after a real person. D-9's
 * guardrail cannot catch it either (it runs on WRITE, never on the default, and
 * matches exact name PARTS, so "Alex" ∉ {"Alexandre"}).
 *
 * ⇒ Use THIS when the name will be presented as *the lawyer's own choice*;
 * use `assistantDisplayName` when something merely has to render.
 *
 * Returns `null` for all FIVE ways of not having a name we may present as his
 * choice — they reach the same answer by different routes and each can regress
 * independently: still `loading` (a mid-fetch name would flicker a wrong
 * instruction), `backendPending` (runtime predates `identity.get`), a failed
 * read (`identity === null` — a failed READ must never become a confident
 * value), `isDefaultAssistantIdentity` (never actually named), and:
 *
 * 🔴 **a STORED name equal to the platform default.** `isDefaultAssistantIdentity`
 * tests `LEGACY_DEFAULT_NAMES = {"lexiun"}` and deliberately answers FALSE for
 * `"Alex"` — a property `AssistantAvatar` depends on and a test pins on purpose,
 * so it must NOT be widened. But it means a stored `"Alex"` reads as
 * lawyer-chosen, and this function cannot tell *he typed it* from *something
 * wrote the default into the field* (`lexiun-user-setup`'s "keep the default"
 * control is an open question that could create exactly that). ⇒ **When the SPA
 * cannot distinguish the lawyer's choice from the product's, it must not assert
 * one.** Found by `tester-galvao` mid-build, 2026-09-14.
 *
 * The cost is one narrow case — a lawyer who genuinely chose "Alex" gets the
 * generic instruction, which still tells him to use his assistant's name, so he
 * types "Alex" anyway. The benefit is that step 2 can never PRINT the collision
 * under the product's endorsement, whichever shape that control takes.
 *
 * ⚠ This is deliberately NOT a value judgement about the name. It is a
 * PROVENANCE test wearing a value test's clothes, for want of provenance on the
 * wire — the same shape as F6's park-kind proxy, and it comes apart the same
 * way. If the identity ever carries "who set this", use THAT and delete this arm.
 *
 * Pure so the rule is unit-testable without rendering.
 * Chain: `ai-docs/lexide-botfather-name-interpolation-miniplan.md`.
 */
export function confirmedAssistantName(
  identity: AssistantIdentity | null,
  opts: { loading: boolean; backendPending: boolean },
): string | null {
  if (opts.loading || opts.backendPending) return null;
  if (isDefaultAssistantIdentity(identity)) return null;
  const name = (identity as AssistantIdentity).name.trim();
  if (name.toLowerCase() === ASSISTANT_DEFAULT_NAME.toLowerCase()) return null;
  return name;
}

/** Initials for the broken-custom-URL fallback roundel (never blank). */
export function assistantInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "A";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function methodPending(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /method|unknown|not.?found|unsupported/i.test(msg);
}

export interface AssistantIdentityState {
  assistant: AssistantIdentity | null;
  owner: Contact | null;
  loading: boolean;
  /** The runtime predates `identity.get` — show the default, not an error. */
  backendPending: boolean;
  refresh: () => void;
}

export function useAssistantIdentity(): AssistantIdentityState {
  const rpc = useGatewayStore((s) => s.rpc);
  const connectionState = useGatewayStore((s) => s.connectionState);
  const [assistant, setAssistant] = useState<AssistantIdentity | null>(null);
  const [owner, setOwner] = useState<Contact | null>(null);
  const [loading, setLoading] = useState(true);
  const [backendPending, setBackendPending] = useState(false);

  const refresh = useCallback(() => {
    if (!rpc || connectionState !== "connected") return;
    rpc
      .identityGet()
      .then((res) => {
        setAssistant(res.assistant);
        setOwner(res.owner);
        setBackendPending(false);
      })
      .catch((err: unknown) => {
        if (methodPending(err)) setBackendPending(true);
      })
      .finally(() => setLoading(false));
  }, [rpc, connectionState]);

  useEffect(() => refresh(), [refresh]);
  useLexEventStream("identity.updated", () => refresh());

  return { assistant, owner, loading, backendPending, refresh };
}
